import * as React from 'react';
import type { TooltipContentProps } from '@radix-ui/react-tooltip';

import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '../ui/tooltip';
import { cn } from '../../lib/utils';

type TriggerProps = Omit<
  React.ComponentProps<typeof TooltipTrigger>,
  'children' | 'asChild' | 'title'
>;

type TooltipRootProps = Omit<
  React.ComponentProps<typeof Tooltip>,
  'children'
>;

type TooltipProviderProps = Omit<
  React.ComponentProps<typeof TooltipProvider>,
  'children' | 'delayDuration'
>;

type TooltipContentAdditionalProps = Omit<
  TooltipContentProps,
  'children' | 'side' | 'align' | 'className'
>;

export interface TooltipWrapperProps extends TriggerProps {
  children: React.ReactNode;
  content?: React.ReactNode;
  side?: TooltipContentProps['side'];
  align?: TooltipContentProps['align'];
  delay?: number;
  disabled?: boolean;
  title?: React.ReactNode;
  placement?: TooltipContentProps['side'];
  fullTitle?: boolean;
  maxLength?: number;
  tooltipProps?: TooltipRootProps;
  providerProps?: TooltipProviderProps;
  contentProps?: TooltipContentAdditionalProps & { className?: string };
}

function setRefValue<T>(
  ref: React.Ref<T> | undefined,
  value: T | null
) {
  if (typeof ref === 'function') {
    ref(value);
    return;
  }

  if (ref) {
    const mutableRef = ref as React.MutableRefObject<T | null>;
    mutableRef.current = value;
  }
}

function mergeRefs<T>(
  ...refs: Array<React.Ref<T> | undefined>
) {
  return (value: T | null) => {
    refs.forEach((ref) => setRefValue(ref, value));
  };
}

interface TriggerWrapperProps extends TriggerProps {
  className?: string;
  tabIndex?: number;
  role?: string;
  children: React.ReactElement;
}

const TooltipTriggerWrapper = React.forwardRef<HTMLElement, TriggerWrapperProps>(
  (
    {
      children, className, tabIndex, role, ...triggerProps
    },
    forwardedRef
  ) => {
    const childElement = React.Children.only(children) as React.ReactElement & {
      ref?: React.Ref<HTMLElement>;
    };

    const elementType = childElement.type;
    const isSvgElement = typeof elementType === 'string' && elementType === 'svg';
    const isIntrinsicElement = typeof elementType === 'string';

    if (!isIntrinsicElement) {
      return (
        <span
          {...triggerProps}
          ref={forwardedRef as React.Ref<HTMLSpanElement>}
          className={cn('tw-inline-flex tw-items-center tw-align-middle', className)}
          tabIndex={tabIndex ?? 0}
          role={role ?? 'button'}
        >
          {React.cloneElement(childElement, {
            className: cn(childElement.props?.className, className),
          })}
        </span>
      );
    }

    const mergedRef = mergeRefs(
      childElement.ref as React.Ref<HTMLElement>,
      forwardedRef
    );

    return React.cloneElement(childElement, {
      ...triggerProps,
      className: cn(childElement.props?.className, className),
      tabIndex: tabIndex ?? (isSvgElement ? 0 : childElement.props?.tabIndex),
      role: role ?? (isSvgElement ? 'button' : childElement.props?.role),
      ref: mergedRef,
    });
  }
);

function resolveContent(
  content: React.ReactNode,
  title: React.ReactNode,
  fullTitle: boolean,
  maxLength: number
) {
  const baseContent = content ?? title ?? null;

  if (
    typeof baseContent === 'string' &&
    !fullTitle &&
    baseContent.length > maxLength
  ) {
    return `${baseContent.substring(0, maxLength)}...`;
  }

  return baseContent;
}

function TooltipWrapperComponent({
  children,
  content,
  title,
  side,
  align = 'center',
  delay = 0,
  disabled = false,
  placement,
  fullTitle = false,
  maxLength = 150,
  tooltipProps,
  providerProps,
  contentProps,
  className,
  tabIndex,
  role,
  ...triggerProps
}: TooltipWrapperProps) {
  if (!children) {
    return null;
  }

  // Normalize children: filter out null/undefined/false and ensure proper structure
  const normalizedChildren = React.Children.toArray(children).filter(
    (child) => child != null && child !== false
  );

  if (normalizedChildren.length === 0) {
    return null;
  }

  if (disabled) {
    // Return children with proper keys if it's an array
    if (normalizedChildren.length > 1) {
      return (
        <>
          {normalizedChildren.map((child, index) => {
            // Preserve key if child already has one, otherwise use index
            const key = React.isValidElement(child) && child.key != null
              ? child.key
              : `tooltip-child-${index}`;
            return <React.Fragment key={key}>{child}</React.Fragment>;
          })}
        </>
      );
    }
    return <>{normalizedChildren[0]}</>;
  }

  const resolvedContent = resolveContent(
    content,
    title,
    fullTitle,
    maxLength
  );

  if (!resolvedContent) {
    // Return children with proper keys if it's an array
    if (normalizedChildren.length > 1) {
      return (
        <>
          {normalizedChildren.map((child, index) => {
            // Preserve key if child already has one, otherwise use index
            const key = React.isValidElement(child) && child.key != null
              ? child.key
              : `tooltip-child-${index}`;
            return <React.Fragment key={key}>{child}</React.Fragment>;
          })}
        </>
      );
    }
    return <>{normalizedChildren[0]}</>;
  }

  const { className: contentClassName, ...contentRest } = contentProps ?? {};
  const resolvedSide = side ?? placement ?? 'top';

  // TooltipTriggerWrapper expects a single child, so use the first one
  const firstChild = normalizedChildren[0];
  const childElement: React.ReactElement = React.isValidElement(firstChild)
    ? (firstChild as React.ReactElement)
    : React.createElement('span', null, firstChild);

  // Normalize resolvedContent to handle arrays and null values
  const normalizedContent = React.Children.toArray(resolvedContent).filter(
    (item) => item != null && item !== false
  );

  // If content is empty after normalization, return children without tooltip
  if (normalizedContent.length === 0) {
    if (normalizedChildren.length > 1) {
      return (
        <>
          {normalizedChildren.map((child, index) => {
            const key = React.isValidElement(child) && child.key != null
              ? child.key
              : `tooltip-child-${index}`;
            return <React.Fragment key={key}>{child}</React.Fragment>;
          })}
        </>
      );
    }
    return <>{normalizedChildren[0]}</>;
  }

  return (
    <Tooltip delayDuration={delay} providerProps={providerProps} {...tooltipProps}>
      <TooltipTrigger asChild>
        <TooltipTriggerWrapper
          {...triggerProps}
          className={className}
          tabIndex={tabIndex}
          role={role}
        >
          {childElement}
        </TooltipTriggerWrapper>
      </TooltipTrigger>
      <TooltipContent
        side={resolvedSide}
        align={align}
        className={contentClassName}
        {...contentRest}
      >
        {normalizedContent.length > 1 ? (
          normalizedContent.map((item, index) => {
            const key = React.isValidElement(item) && item.key != null
              ? item.key
              : `tooltip-content-${index}`;
            return <React.Fragment key={key}>{item}</React.Fragment>;
          })
        ) : (
          normalizedContent[0]
        )}
      </TooltipContent>
    </Tooltip>
  );
}

export const TooltipWrapper = React.memo(TooltipWrapperComponent);

export default TooltipWrapper;

